import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";

export interface SystemNotification {
  id: string;
  title: string;
  content: string;
  type: 'info' | 'warning' | 'success' | 'announcement';
  target_audience: string;
  created_at: string;
  expires_at?: string;
  is_read?: boolean;
  metadata?: {
    ticket_id?: string;
    interaction_type?: 'message' | 'status_change' | 'assignment' | 'priority_change';
    redirect_url?: string;
    [key: string]: any;
  };
}

export function useSystemNotifications() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["system-notifications", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data: notifications, error } = await supabase
        .from("system_notifications")
        .select(`
          id,
          title,
          content,
          type,
          target_audience,
          created_at,
          expires_at,
          metadata,
          user_notification_reads!left(id, read_at)
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;

      return notifications.map((notification: any) => ({
        ...notification,
        is_read: notification.user_notification_reads.length > 0
      }));
    },
    enabled: !!user?.id,
  });
}

export function useUnreadNotificationCount() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["unread-notification-count", user?.id],
    queryFn: async () => {
      if (!user?.id) return 0;

      const { data, error } = await supabase.rpc("get_unread_notification_count");
      
      if (error) throw error;
      return data || 0;
    },
    enabled: !!user?.id,
    refetchInterval: 30000, // Refresh every 30 seconds
  });
}

export function useMarkNotificationAsRead() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      if (!user?.id) throw new Error("User not authenticated");

      const { error } = await supabase
        .from("user_notification_reads")
        .insert({
          user_id: user.id,
          notification_id: notificationId,
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["system-notifications"] });
      queryClient.invalidateQueries({ queryKey: ["unread-notification-count"] });
    },
  });
}

export function useRecentAuditLogs(companyId?: string) {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ["recent-audit-logs", companyId, user?.id],
    queryFn: async () => {
      if (!companyId && !user?.id) return [];

      let query = supabase
        .from("audit_logs")
        .select(`
          id,
          action,
          entity_type,
          created_at,
          meta,
          profiles!actor_user_id(full_name)
        `)
        .in("action", [
          "user.create",
          "user.invite", 
          "vehicle.create",
          "driver.create",
          "trip.create",
          "work_order.create",
          "plan.upgrade",
          "plan.downgrade",
          "ticket.message",
          "ticket.status_change",
          "ticket.assignment",
          "ticket.priority_change"
        ])
        .order("created_at", { ascending: false })
        .limit(10);

      // Filtrar por company_id se disponível, senão por user_id
      if (companyId) {
        query = query.eq("company_id", companyId);
      } else if (user?.id) {
        query = query.eq("user_id", user.id);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    },
    enabled: !!(companyId || user?.id),
  });
}